import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  Chip,
  Stack,
  Alert,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Link,
  IconButton,
  Autocomplete,
  TextField,
  createFilterOptions,
} from "@mui/material";
import {
  Psychology as PsychologyIcon,
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  AutoAwesome as AutoAwesomeIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { PlaidTransaction } from "../../services/external";
import {
  transactionCategoriesDB,
  LearnedPattern,
} from "../../services/database/transactionCategoriesDB";
import {
  findMerchantMatch,
  getConfidenceDescription,
} from "../../services/categorization/merchantKnowledge";
import { mapPlaidCategory } from "../../services/categorization/plaidCategoryMapping";
import {
  getTaxCategorySuggestions,
  shouldAutoApplyTaxCategories,
  getTaxCategoryExplanation,
} from "../../services/categorization/categoryMappingRules";

// Filter for General Category with "Add new" option
const generalCategoryFilter = createFilterOptions<string>();

interface UserCategorizationModalProps {
  open: boolean;
  transaction: PlaidTransaction | null;
  learnedPatterns?: LearnedPattern[];
  allTransactions?: PlaidTransaction[];
  generalCategories: string[];
  federalCategories: { id: string; name: string; description: string }[];
  oregonCategories: { id: string; name: string; description: string }[];
  onCategorize: (
    transactionId: string,
    generalCategory: string,
    federalCategory: string | null,
    oregonCategory: string | null,
    applyToSimilar: boolean
  ) => void;
  onSkip: () => void;
  onClose?: () => void;
  currentIndex: number;
  totalCount: number;
}

export const UserCategorizationModal: React.FC<
  UserCategorizationModalProps
> = ({
  open,
  transaction,
  learnedPatterns = [],
  allTransactions = [],
  generalCategories,
  federalCategories,
  oregonCategories,
  onCategorize,
  onSkip,
  onClose,
  currentIndex,
  totalCount,
}) => {
  const [generalCategory, setGeneralCategory] = useState("");
  const [federalCategory, setFederalCategory] = useState("");
  const [oregonCategory, setOregonCategory] = useState("");
  const [applyToSimilar, setApplyToSimilar] = useState(true);
  const [ruleSearch, setRuleSearch] = useState("");
  const [ruleCategoryFilter, setRuleCategoryFilter] = useState("all");
  const [showSimilarList, setShowSimilarList] = useState(false);
  const [suggestedPattern, setSuggestedPattern] = useState<any>(null);
  const [merchantSuggestion, setMerchantSuggestion] = useState<any>(null);
  const [plaidSuggestion, setPlaidSuggestion] = useState<any>(null);
  const [appliedSuggestion, setAppliedSuggestion] = useState<
    "merchant" | "plaid" | "pattern" | null
  >(null);
  const remainingCount = Math.max(totalCount - currentIndex + 1, 0);

  // Reset state when transaction changes
  useEffect(() => {
    if (transaction) {
      setGeneralCategory("");
      setFederalCategory("");
      setOregonCategory("");
      setAppliedSuggestion(null);
      setShowSimilarList(false);

      // Look for suggested patterns
      const findSuggestedPattern = async () => {
        const patterns = await transactionCategoriesDB.findMatchingPatterns(
          transaction.merchant_name,
          transaction.name
        );

        if (patterns.length > 0) {
          setSuggestedPattern(patterns[0]);
          // Pre-fill with suggested values
          setGeneralCategory(patterns[0].general_category);
          setFederalCategory(patterns[0].federal_category || "");
          setOregonCategory(patterns[0].oregon_category || "");
          setAppliedSuggestion("pattern");
        } else {
          setSuggestedPattern(null);
        }
      };

      findSuggestedPattern();

      // Check merchant knowledge base
      const merchantMatch = findMerchantMatch(
        transaction.merchant_name,
        transaction.name
      );
      setMerchantSuggestion(merchantMatch);

      // Check Plaid category mapping
      const plaidMapping = mapPlaidCategory(transaction.category);
      setPlaidSuggestion(plaidMapping);

      // Auto-fill with highest confidence suggestion
      if (merchantMatch && merchantMatch.confidence >= 0.9) {
        setGeneralCategory(merchantMatch.general_category);
        setFederalCategory(merchantMatch.federal_category || "");
        setOregonCategory(merchantMatch.oregon_category || "");
        setAppliedSuggestion("merchant");
      } else if (
        plaidMapping &&
        plaidMapping.confidence >= 0.8 &&
        !plaidMapping.requires_context
      ) {
        setGeneralCategory(plaidMapping.general_category);
        setFederalCategory(plaidMapping.federal_category || "");
        setOregonCategory(plaidMapping.oregon_category || "");
        setAppliedSuggestion("plaid");
      } else {
        setAppliedSuggestion(null);
      }
    }
  }, [transaction]);

  const handleSubmit = () => {
    if (!transaction || !generalCategory) return;

    onCategorize(
      transaction.transaction_id,
      generalCategory,
      federalCategory || null,
      oregonCategory || null,
      applyToSimilar
    );
  };

  // Auto-select tax categories when general category changes
  const handleGeneralCategoryChange = (newCategory: string) => {
    setGeneralCategory(newCategory);

    // Get AI suggestions for tax categories based on general category
    const taxSuggestions = getTaxCategorySuggestions(newCategory);

    if (taxSuggestions) {
      // Only auto-apply if confidence is high enough
      if (shouldAutoApplyTaxCategories(taxSuggestions)) {
        setFederalCategory(taxSuggestions.federalCategory || "");
        setOregonCategory(taxSuggestions.oregonCategory || "");
      } else if (taxSuggestions.confidence >= 0.6) {
        // For medium confidence, pre-fill but let user review
        setFederalCategory(taxSuggestions.federalCategory || "");
        setOregonCategory(taxSuggestions.oregonCategory || "");
      }
    } else {
      // Clear tax categories if no mapping found
      setFederalCategory("");
      setOregonCategory("");
    }
  };

  const ruleCategories = useMemo(() => {
    const categories = learnedPatterns
      .map((pattern) => pattern.general_category)
      .filter(Boolean);
    return Array.from(new Set(categories)).sort((a, b) => a.localeCompare(b));
  }, [learnedPatterns]);

  const filteredRules = useMemo(() => {
    const search = ruleSearch.trim().toLowerCase();
    return learnedPatterns.filter((pattern) => {
      if (ruleCategoryFilter !== "all") {
        if (pattern.general_category !== ruleCategoryFilter) return false;
      }
      if (!search) return true;
      const haystack = [
        pattern.merchant_pattern,
        pattern.transaction_pattern,
        pattern.general_category,
        pattern.federal_category,
        pattern.oregon_category,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(search);
    });
  }, [learnedPatterns, ruleCategoryFilter, ruleSearch]);

  const similarTransactions = useMemo(() => {
    if (!transaction) return [];
    const merchantKey = transaction.merchant_name?.trim();
    const nameKey = transaction.name?.trim();
    if (!merchantKey && !nameKey) return [];
    return allTransactions.filter((item) => {
      if (item.transaction_id === transaction.transaction_id) return false;
      const merchantMatch =
        merchantKey && item.merchant_name?.trim() === merchantKey;
      const nameMatch = nameKey && item.name?.trim() === nameKey;
      return Boolean(merchantMatch || nameMatch);
    });
  }, [allTransactions, transaction]);

  useEffect(() => {
    console.log("[HelpMeLearn] Rule filter", {
      search: ruleSearch,
      category: ruleCategoryFilter,
      total: learnedPatterns.length,
      filtered: filteredRules.length,
    });
  }, [
    filteredRules.length,
    learnedPatterns.length,
    ruleCategoryFilter,
    ruleSearch,
  ]);

  if (!transaction) return null;

  const progress = (currentIndex / totalCount) * 100;
  const similarCount = similarTransactions.length;
  const handleSimilarClick = () => {
    if (similarCount === 0) return;
    console.log("[HelpMeLearn] Showing similar transactions list", {
      similarCount,
      transactionId: transaction.transaction_id,
    });
    setShowSimilarList(true);
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 24,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SchoolIcon color="primary" />
            <Typography variant="h6">Categorize wtih AI</Typography>
          </Box>
          {onClose && (
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ ml: 2 }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{ mt: 2, height: 6, borderRadius: 3 }}
        />
        <Typography variant="caption" color="text.secondary">
          {remainingCount} remaining
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "280px 1fr" },
            gap: 3,
          }}
        >
          <Box
            sx={{
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              p: 2,
              height: "100%",
              maxHeight: { md: 640 },
              overflow: "auto",
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Existing Rules
            </Typography>
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              <TextField
                size="small"
                label="Search rules"
                value={ruleSearch}
                onChange={(event) => setRuleSearch(event.target.value)}
              />
              <FormControl size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={ruleCategoryFilter}
                  onChange={(event) =>
                    setRuleCategoryFilter(event.target.value)
                  }
                >
                  <MenuItem value="all">All</MenuItem>
                  {ruleCategories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            {filteredRules.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                No rules found.
              </Typography>
            ) : (
              <Stack spacing={1.5}>
                {filteredRules.map((pattern) => (
                  <Box
                    key={
                      pattern.id ||
                      `${pattern.merchant_pattern}-${pattern.transaction_pattern}`
                    }
                    sx={{
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {pattern.general_category}
                    </Typography>
                    {pattern.merchant_pattern && (
                      <Typography variant="caption" color="text.secondary">
                        Merchant: {pattern.merchant_pattern}
                      </Typography>
                    )}
                    {pattern.transaction_pattern && (
                      <Typography variant="caption" color="text.secondary">
                        Name: {pattern.transaction_pattern}
                      </Typography>
                    )}
                    {(pattern.federal_category || pattern.oregon_category) && (
                      <Typography variant="caption" color="text.secondary">
                        {pattern.federal_category
                          ? `Federal: ${pattern.federal_category}`
                          : ""}
                        {pattern.federal_category && pattern.oregon_category
                          ? " • "
                          : ""}
                        {pattern.oregon_category
                          ? `Oregon: ${pattern.oregon_category}`
                          : ""}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            )}
          </Box>

          <Box sx={{ overflow: "hidden" }}>
            <Box
              sx={{
                display: "flex",
                width: "200%",
                transform: showSimilarList
                  ? "translateX(-50%)"
                  : "translateX(0)",
                transition: "transform 0.35s ease",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  pr: { md: 2 },
                  boxSizing: "border-box",
                }}
              >
                {/* Transaction Details */}
                <Alert severity="info" icon={<PsychologyIcon />} sx={{ mb: 3 }}>
                  <Typography variant="subtitle2">
                    Help me understand how to categorize this transaction:
                  </Typography>
                </Alert>

                <Box
                  sx={{
                    p: 2,
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    border: 1,
                    borderColor: "divider",
                    mb: 3,
                  }}
                >
                  <Stack spacing={1}>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="subtitle2" color="text.secondary">
                        Merchant:
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {transaction.merchant_name || transaction.name}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="subtitle2" color="text.secondary">
                        Amount:
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight="medium"
                        color={
                          transaction.amount < 0 ? "success.main" : "error.main"
                        }
                      >
                        ${Math.abs(transaction.amount).toFixed(2)}
                        {transaction.amount < 0 ? " (Credit)" : " (Debit)"}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="subtitle2" color="text.secondary">
                        Date:
                      </Typography>
                      <Typography variant="body1">
                        {transaction.date}
                      </Typography>
                    </Box>
                    {transaction.category && (
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography variant="subtitle2" color="text.secondary">
                          Plaid Category:
                        </Typography>
                        <Chip
                          label={
                            Array.isArray(transaction.category)
                              ? transaction.category.join(" > ")
                              : transaction.category
                          }
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    )}
                  </Stack>
                </Box>

                {/* AI Suggestions Section */}
                {(merchantSuggestion || plaidSuggestion) && (
                  <Box sx={{ mb: 3 }}>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      AI Suggestions:
                    </Typography>
                    <Stack spacing={1}>
                      {merchantSuggestion && (
                        <Alert
                          severity={
                            merchantSuggestion.confidence >= 0.9
                              ? "success"
                              : "info"
                          }
                          sx={{ py: 1 }}
                        >
                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight="medium">
                                  🏪 Merchant Match:{" "}
                                  {merchantSuggestion.merchant_name ||
                                    transaction.merchant_name}
                                </Typography>
                                <Typography variant="body2">
                                  {merchantSuggestion.general_category}
                                  {merchantSuggestion.federal_category &&
                                    ` | Federal: ${merchantSuggestion.federal_category}`}
                                  {merchantSuggestion.oregon_category &&
                                    ` | Oregon: ${merchantSuggestion.oregon_category}`}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Confidence:{" "}
                                  {getConfidenceDescription(
                                    merchantSuggestion.confidence
                                  )}{" "}
                                  (
                                  {Math.round(
                                    merchantSuggestion.confidence * 100
                                  )}
                                  %)
                                  {merchantSuggestion.notes &&
                                    ` - ${merchantSuggestion.notes}`}
                                </Typography>
                              </Box>
                              {appliedSuggestion !== "merchant" && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => {
                                    setGeneralCategory(
                                      merchantSuggestion.general_category
                                    );
                                    setFederalCategory(
                                      merchantSuggestion.federal_category || ""
                                    );
                                    setOregonCategory(
                                      merchantSuggestion.oregon_category || ""
                                    );
                                    setAppliedSuggestion("merchant");
                                  }}
                                  sx={{ ml: 2, minWidth: "auto" }}
                                >
                                  Apply
                                </Button>
                              )}
                              {appliedSuggestion === "merchant" && (
                                <Chip
                                  label="Applied"
                                  size="small"
                                  color="success"
                                  icon={<CheckCircleIcon />}
                                  sx={{ ml: 2 }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Alert>
                      )}

                      {plaidSuggestion && (
                        <Alert
                          severity={
                            plaidSuggestion.confidence >= 0.8
                              ? "success"
                              : "info"
                          }
                          sx={{ py: 1 }}
                        >
                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                              }}
                            >
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" fontWeight="medium">
                                  🏦 Plaid Category Suggestion
                                </Typography>
                                <Typography variant="body2">
                                  {plaidSuggestion.general_category}
                                  {plaidSuggestion.federal_category &&
                                    ` | Federal: ${plaidSuggestion.federal_category}`}
                                  {plaidSuggestion.oregon_category &&
                                    ` | Oregon: ${plaidSuggestion.oregon_category}`}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  Confidence:{" "}
                                  {Math.round(plaidSuggestion.confidence * 100)}
                                  %
                                  {plaidSuggestion.requires_context &&
                                    ` - ${
                                      plaidSuggestion.context_hint ||
                                      "May require context"
                                    }`}
                                </Typography>
                              </Box>
                              {appliedSuggestion !== "plaid" && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  onClick={() => {
                                    setGeneralCategory(
                                      plaidSuggestion.general_category
                                    );
                                    setFederalCategory(
                                      plaidSuggestion.federal_category || ""
                                    );
                                    setOregonCategory(
                                      plaidSuggestion.oregon_category || ""
                                    );
                                    setAppliedSuggestion("plaid");
                                  }}
                                  sx={{ ml: 2, minWidth: "auto" }}
                                >
                                  Apply
                                </Button>
                              )}
                              {appliedSuggestion === "plaid" && (
                                <Chip
                                  label="Applied"
                                  size="small"
                                  color="success"
                                  icon={<CheckCircleIcon />}
                                  sx={{ ml: 2 }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Alert>
                      )}
                    </Stack>
                  </Box>
                )}

                {/* Suggested Pattern */}
                {suggestedPattern && (
                  <Alert severity="success" sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2">
                          I found a similar pattern from your previous
                          categorizations:
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          General: {suggestedPattern.general_category}
                          {suggestedPattern.federal_category &&
                            ` | Federal: ${suggestedPattern.federal_category}`}
                          {suggestedPattern.oregon_category &&
                            ` | Oregon: ${suggestedPattern.oregon_category}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Used {suggestedPattern.times_used} times with{" "}
                          {Math.round(suggestedPattern.confidence_score * 100)}%
                          confidence
                        </Typography>
                      </Box>
                      {appliedSuggestion !== "pattern" && (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          onClick={() => {
                            setGeneralCategory(
                              suggestedPattern.general_category
                            );
                            setFederalCategory(
                              suggestedPattern.federal_category || ""
                            );
                            setOregonCategory(
                              suggestedPattern.oregon_category || ""
                            );
                            setAppliedSuggestion("pattern");
                          }}
                          sx={{ ml: 2, minWidth: "auto" }}
                        >
                          Apply
                        </Button>
                      )}
                      {appliedSuggestion === "pattern" && (
                        <Chip
                          label="Applied"
                          size="small"
                          color="success"
                          icon={<CheckCircleIcon />}
                          sx={{ ml: 2 }}
                        />
                      )}
                    </Box>
                  </Alert>
                )}

                {/* Applied Suggestion Indicator */}
                {appliedSuggestion && (
                  <Alert
                    severity="success"
                    icon={<AutoAwesomeIcon />}
                    sx={{ mb: 3 }}
                  >
                    <Typography variant="body2">
                      Categories have been pre-filled based on{" "}
                      {appliedSuggestion === "merchant"
                        ? "merchant match"
                        : appliedSuggestion === "plaid"
                          ? "Plaid category"
                          : "previous pattern"}
                      . Review and confirm below.
                    </Typography>
                  </Alert>
                )}

                {/* AI Tax Category Suggestion - shows when user selects general category */}
                {generalCategory &&
                  !appliedSuggestion &&
                  (() => {
                    const taxSuggestion =
                      getTaxCategorySuggestions(generalCategory);
                    if (
                      taxSuggestion &&
                      (taxSuggestion.federalCategory ||
                        taxSuggestion.oregonCategory)
                    ) {
                      const federalName = taxSuggestion.federalCategory
                        ? federalCategories.find(
                            (c) => c.id === taxSuggestion.federalCategory
                          )?.name
                        : null;
                      const oregonName = taxSuggestion.oregonCategory
                        ? oregonCategories.find(
                            (c) => c.id === taxSuggestion.oregonCategory
                          )?.name
                        : null;
                      return (
                        <Alert
                          severity={
                            taxSuggestion.confidence >= 0.8 ? "success" : "info"
                          }
                          icon={<AutoAwesomeIcon />}
                          sx={{ mb: 2 }}
                        >
                          <Typography variant="body2" fontWeight="medium">
                            AI Tax Category Suggestion for "{generalCategory}":
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {federalName && `Federal: ${federalName}`}
                            {federalName && oregonName && " | "}
                            {oregonName && `Oregon: ${oregonName}`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {getTaxCategoryExplanation(taxSuggestion)}{" "}
                            (Confidence:{" "}
                            {Math.round(taxSuggestion.confidence * 100)}%)
                          </Typography>
                        </Alert>
                      );
                    }
                    return null;
                  })()}

                {/* Category Selection */}
                <Stack spacing={3}>
                  {/* General Category */}
                  <Autocomplete
                    freeSolo
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    options={generalCategories}
                    value={generalCategory}
                    onChange={(_, newValue) => {
                      if (typeof newValue === "string") {
                        // Handle selecting an existing option or typing a new value
                        if (
                          newValue.startsWith('Add "') &&
                          newValue.endsWith('"')
                        ) {
                          // Extract the new category name from 'Add "X"'
                          const newCategory = newValue.slice(5, -1);
                          handleGeneralCategoryChange(newCategory);
                        } else {
                          handleGeneralCategoryChange(newValue);
                        }
                      } else {
                        handleGeneralCategoryChange("");
                      }
                    }}
                    filterOptions={(options, params) => {
                      const filtered = generalCategoryFilter(options, params);
                      const { inputValue } = params;
                      // Suggest creating a new value if it doesn't exist
                      const isExisting = options.some(
                        (option) =>
                          inputValue.toLowerCase() === option.toLowerCase()
                      );
                      if (inputValue !== "" && !isExisting) {
                        filtered.push(`Add "${inputValue}"`);
                      }
                      return filtered;
                    }}
                    renderOption={(props, option) => {
                      // Style the "Add X" option differently
                      if (option.startsWith('Add "')) {
                        return (
                          <li {...props} key={option}>
                            <Typography
                              sx={{
                                fontStyle: "italic",
                                color: "primary.main",
                              }}
                            >
                              {option}
                            </Typography>
                          </li>
                        );
                      }
                      return (
                        <li {...props} key={option}>
                          {option}
                        </li>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="General Category"
                        required
                        placeholder="Type to search or add new..."
                      />
                    )}
                    ListboxProps={{
                      style: { maxHeight: 300 },
                    }}
                  />

                  {/* Federal Tax Category */}
                  <Autocomplete
                    freeSolo
                    options={federalCategories}
                    getOptionLabel={(option) => {
                      if (typeof option === "string") return option;
                      return option.name || "";
                    }}
                    value={
                      federalCategories.find(
                        (cat) => cat.id === federalCategory
                      ) ||
                      (federalCategory
                        ? {
                            id: federalCategory,
                            name: federalCategory,
                            description: "",
                          }
                        : null)
                    }
                    onChange={(_, newValue) => {
                      if (typeof newValue === "string") {
                        setFederalCategory(newValue);
                      } else if (newValue) {
                        setFederalCategory(newValue.id);
                      } else {
                        setFederalCategory("");
                      }
                    }}
                    onInputChange={(_, newInputValue, reason) => {
                      if (reason === "input" && newInputValue) {
                        // Check if it matches an existing category
                        const match = federalCategories.find(
                          (cat) =>
                            cat.name.toLowerCase() ===
                            newInputValue.toLowerCase()
                        );
                        if (!match) {
                          setFederalCategory(newInputValue);
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Federal Tax Category (Optional)"
                        placeholder="Type to search or add new..."
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Box>
                          <Typography variant="body2">{option.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.description}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    isOptionEqualToValue={(option, value) => {
                      if (typeof value === "string")
                        return option.id === value || option.name === value;
                      return option.id === value?.id;
                    }}
                    ListboxProps={{
                      style: { maxHeight: 300 },
                    }}
                  />

                  {/* Oregon Tax Category */}
                  <Autocomplete
                    freeSolo
                    options={oregonCategories}
                    getOptionLabel={(option) => {
                      if (typeof option === "string") return option;
                      return option.name || "";
                    }}
                    value={
                      oregonCategories.find(
                        (cat) => cat.id === oregonCategory
                      ) ||
                      (oregonCategory
                        ? {
                            id: oregonCategory,
                            name: oregonCategory,
                            description: "",
                          }
                        : null)
                    }
                    onChange={(_, newValue) => {
                      if (typeof newValue === "string") {
                        setOregonCategory(newValue);
                      } else if (newValue) {
                        setOregonCategory(newValue.id);
                      } else {
                        setOregonCategory("");
                      }
                    }}
                    onInputChange={(_, newInputValue, reason) => {
                      if (reason === "input" && newInputValue) {
                        // Check if it matches an existing category
                        const match = oregonCategories.find(
                          (cat) =>
                            cat.name.toLowerCase() ===
                            newInputValue.toLowerCase()
                        );
                        if (!match) {
                          setOregonCategory(newInputValue);
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Oregon Tax Category (Optional)"
                        placeholder="Type to search or add new..."
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Box>
                          <Typography variant="body2">{option.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.description}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    isOptionEqualToValue={(option, value) => {
                      if (typeof value === "string")
                        return option.id === value || option.name === value;
                      return option.id === value?.id;
                    }}
                    ListboxProps={{
                      style: { maxHeight: 300 },
                    }}
                  />

                  {/* Apply to Similar */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={applyToSimilar}
                        onChange={(e) => setApplyToSimilar(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Apply this to{" "}
                        <Link
                          component="button"
                          type="button"
                          onClick={handleSimilarClick}
                          underline="always"
                          aria-disabled={similarCount === 0}
                          sx={{
                            fontWeight: 600,
                            cursor: similarCount === 0 ? "default" : "pointer",
                            color:
                              similarCount === 0
                                ? "text.secondary"
                                : "primary.main",
                          }}
                        >
                          {similarCount}
                        </Link>{" "}
                        similar transactions.
                      </Typography>
                    }
                  />
                </Stack>
              </Box>
              <Box
                sx={{
                  width: "100%",
                  pl: { md: 2 },
                  boxSizing: "border-box",
                }}
              >
                <Stack spacing={2}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="subtitle1">
                      Similar Transactions
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setShowSimilarList(false)}
                    >
                      Back
                    </Button>
                  </Box>
                  {similarCount === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No similar transactions found.
                    </Typography>
                  ) : (
                    <Stack spacing={1.5}>
                      {similarTransactions.map((item) => (
                        <Box
                          key={item.transaction_id}
                          sx={{
                            border: 1,
                            borderColor: "divider",
                            borderRadius: 1,
                            p: 1,
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {item.merchant_name || item.name}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mt: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {item.date}
                            </Typography>
                            <Typography
                              variant="caption"
                              color={
                                item.amount < 0 ? "success.main" : "error.main"
                              }
                            >
                              ${Math.abs(item.amount).toFixed(2)}{" "}
                              {item.amount < 0 ? "Credit" : "Debit"}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onSkip} color="inherit">
          Skip This One
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!generalCategory}
          startIcon={<CheckCircleIcon />}
        >
          {currentIndex === totalCount ? "Save & Finish" : "Save & Continue"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
